import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Label
} from 'shared/components/ui/label';
import {
    cn
} from 'shared/lib/utils';

/**
 * TypedField — schema-driven input row.
 *
 * Single primitive both Assistant entity-schema fields AND OpCode `inputs[]`
 * use, so a "string with pattern", "integer 1-100", "boolean", "object",
 * "array", or "enum" looks the same everywhere.
 *
 * Props mirror the SCHEMAS.md TypedInput shape:
 *   { name, type, required, default, description, constraint }
 *
 * Value is always a string in/out of the form (the parent JSON.parses
 * non-string types at submit time). Keeps state simple, allows users to
 * type expressions (e.g. JSON), and makes "blank" semantically distinct
 * from "null".
 */
export default function TypedField({
    name,
    type = 'string',
    required = false,
    description,
    constraint,
    value,
    onChange,
    badge // optional override for the right-side hint, e.g. "implicit"
}) {
    const enumValues = Array.isArray(constraint?.enum) ? constraint.enum : null;
    const usesSelect = enumValues && enumValues.length > 0;
    const usesSwitch = type === 'boolean';
    const usesTextarea = type === 'object' || type === 'array';
    const usesNumber = type === 'number' || type === 'integer';

    const labelText = description ? '' : null;
    const hint = constraintHint(type, constraint);

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`tf-${name}`} className="flex items-center gap-1.5 text-[11.5px] text-foreground">
                    <span className="font-medium">{name}</span>
                    <span className="text-[10px] font-normal text-muted-foreground">{typeLabel(type)}</span>
                    {required && <span className="text-destructive" title="required">*</span>}
                </Label>
                {(badge || hint) && (
                    <span className={cn(
                        'truncate text-[10px] tracking-wide',
                        badge ? 'text-amber-700' : 'text-muted-foreground font-mono'
                    )}>
                        {badge || hint}
                    </span>
                )}
            </div>

            {usesSelect && (
                <Select value={String(value ?? '')} onValueChange={(v) => onChange(v)}>
                    <SelectTrigger id={`tf-${name}`} className="h-8 text-[12.5px]">
                        <SelectValue placeholder={`Pick ${name}`} />
                    </SelectTrigger>
                    <SelectContent>
                        {enumValues.map((opt) => (
                            <SelectItem key={String(opt)} value={String(opt)}>
                                <span className="text-[12.5px]">{String(opt)}</span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}

            {!usesSelect && usesSwitch && (
                <div className="flex items-center gap-2 pt-0.5">
                    <Switch
                        id={`tf-${name}`}
                        checked={value === 'true' || value === true}
                        onCheckedChange={(c) => onChange(c ? 'true' : 'false')}
                    />
                    <span className="text-[11.5px] text-muted-foreground">
                        {(value === 'true' || value === true) ? 'true' : 'false'}
                    </span>
                </div>
            )}

            {!usesSelect && !usesSwitch && usesTextarea && (() => {
                const raw = (value ?? '').toString();
                const isBlank = raw.trim() === '';
                let parseOk = true;
                let parseShapeOk = true;
                if (!isBlank) {
                    try {
                        const parsed = JSON.parse(raw);
                        parseShapeOk = type === 'array' ? Array.isArray(parsed) : (parsed && typeof parsed === 'object' && !Array.isArray(parsed));
                    } catch {
                        parseOk = false;
                    }
                }
                const invalid = !isBlank && (!parseOk || !parseShapeOk);
                return (
                    <>
                        <Textarea
                            id={`tf-${name}`}
                            value={raw}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={type === 'array'
                                ? 'JSON array — e.g. ["a", "b"] or [1, 2, 3]'
                                : 'JSON object — e.g. { "key": "value" }'}
                            rows={3}
                            className={cn(
                                'resize-y text-[12px] font-mono',
                                invalid && 'border-destructive focus-visible:ring-destructive/40'
                            )}
                            aria-invalid={invalid}
                        />
                        {invalid && (
                            <p className="text-[10.5px] leading-snug text-destructive">
                                {!parseOk
                                    ? `Not valid JSON. For an ${type}, type something like `
                                    : `Valid JSON but not an ${type}. Type something like `}
                                <code className="font-mono">{type === 'array' ? '["hi", "there"]' : '{ "k": "v" }'}</code>
                                .
                            </p>
                        )}
                    </>
                );
            })()}

            {!usesSelect && !usesSwitch && !usesTextarea && (
                <Input
                    id={`tf-${name}`}
                    type={usesNumber ? 'number' : 'text'}
                    inputMode={usesNumber ? 'decimal' : undefined}
                    step={type === 'integer' ? '1' : undefined}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholderFor(type, constraint, name)}
                    className="h-8 text-[12.5px]"
                />
            )}

            {description && (
                <p className="text-[10.5px] leading-snug text-muted-foreground">{description}</p>
            )}
            {labelText}
        </div>
    );
}

function typeLabel(type) {
    return `· ${type || 'string'}`;
}

function constraintHint(type, constraint) {
    if (!constraint || typeof constraint !== 'object') return null;
    const bits = [];
    if (constraint.pattern) bits.push(constraint.pattern);
    if (constraint.minLength != null || constraint.maxLength != null) {
        const lo = constraint.minLength ?? 0;
        const hi = constraint.maxLength ?? '∞';
        bits.push(`len ${lo}..${hi}`);
    }
    if (constraint.minimum != null || constraint.maximum != null) {
        const lo = constraint.minimum ?? '−∞';
        const hi = constraint.maximum ?? '+∞';
        bits.push(`${lo}..${hi}`);
    }
    if (Array.isArray(constraint.enum) && constraint.enum.length > 0) {
        bits.push(`one of ${constraint.enum.length}`);
    }
    return bits.join(' · ') || null;
}

function placeholderFor(type, constraint, name) {
    if (constraint?.pattern) return `match ${constraint.pattern}`;
    if (type === 'number' || type === 'integer') return type === 'integer' ? '0' : '0.0';
    return name; // empty Input: just show field name as a hint
}

/**
 * Coerce a string field value to the right runtime type at submit time.
 * Empty stays as "" (skipped by callers).
 */
export function coerceTypedFieldValue(raw, type) {
    if (raw === '' || raw == null) return '';
    if (type === 'boolean') return raw === 'true' || raw === true;
    if (type === 'integer') {
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : raw;
    }
    if (type === 'number') {
        const n = Number(raw);
        return Number.isFinite(n) ? n : raw;
    }
    if (type === 'object' || type === 'array') {
        try { return JSON.parse(raw); } catch { return raw; }
    }
    return raw;
}
